import { Link } from "react-router-dom";
import { ArrowUpRight, Sparkles } from "lucide-react";
import { PORTFOLIO, PORTFOLIO_EXTENDED } from "@/lib/constants";
import { useReveal } from "@/lib/useReveal";
import { Pic } from "@/components/Pic";

// First-impression strip: shows 8 real product thumbnails right after hero,
// so visitors instantly see this is a wedding stationery printing atelier.
export default function ProductStrip() {
  const { ref } = useReveal();
  // Mix featured + extended for best variety
  const items = [
    ...PORTFOLIO.slice(0, 4),
    ...PORTFOLIO_EXTENDED.slice(0, 4),
  ];

  return (
    <section
      ref={ref}
      data-testid="section-product-strip"
      className="reveal relative border-y border-[#3A2530] bg-[#0E0709]"
    >
      <div className="max-w-[1500px] mx-auto px-4 md:px-10 lg:px-16 py-10 md:py-14">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-6 md:mb-8">
          <div>
            <p className="overline text-[#F0C4BC] mb-2 inline-flex items-center gap-2">
              <Sparkles className="w-3 h-3" /> Wedding Stationery · Printed in-house
            </p>
            <h2 className="font-heading text-2xl md:text-4xl lg:text-5xl font-normal leading-tight text-[#F8EDE7]">
              Invitations. Itineraries. <span className="italic-serif text-[#F0C4BC]">Keepsakes.</span>
            </h2>
          </div>
          <Link
            to="/services"
            data-testid="product-strip-view-all"
            className="text-[11px] md:text-[12px] tracking-[0.22em] uppercase text-[#F8EDE7] hover:text-[#F0C4BC] inline-flex items-center gap-2 border-b border-[#F8EDE7] hover:border-[#F0C4BC] pb-1 transition-colors"
          >
            View all <ArrowUpRight className="w-3.5 h-3.5" />
          </Link>
        </div>

        {/* Horizontal scroll strip — works beautifully on mobile */}
        <div className="flex gap-3 md:gap-4 overflow-x-auto snap-x snap-mandatory scrollbar-thin -mx-4 px-4 md:mx-0 md:px-0 pb-2">
          {items.map((p, i) => (
            <Link
              key={p.id}
              to="/services"
              data-testid={`product-strip-${p.id}`}
              className="group relative shrink-0 snap-start w-[42vw] sm:w-[28vw] md:w-[180px] lg:w-[200px] border border-[#3A2530] hover:border-[#F0C4BC] transition-colors bg-[#1A0E12] overflow-hidden tilt-card card-shine"
              style={{ animationDelay: `${i * 0.05}s` }}
            >
              <div className="aspect-[4/5] overflow-hidden">
                <Pic
                  src={p.img}
                  alt={`${p.title} — ${p.cat} premium wedding stationery printed in-house by StationeryWalaa`}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-[#0E0709] via-[#0E0709]/20 to-transparent opacity-90" />
              <div className="absolute bottom-0 left-0 right-0 p-3 md:p-4">
                <p className="text-[9px] md:text-[10px] tracking-[0.18em] uppercase text-[#F0C4BC] mb-1">
                  {p.cat}
                </p>
                <p className="font-heading text-sm md:text-base text-[#F8EDE7] leading-tight italic-serif truncate">
                  {p.title}
                </p>
              </div>
            </Link>
          ))}
        </div>

        <p className="mt-4 text-center md:text-left text-[10px] tracking-[0.2em] uppercase text-[#C4A8B0]">
          ← Swipe to see more ·  500+ designs crafted for Indian weddings
        </p>
      </div>
    </section>
  );
}
